import { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "../../lib/api";

export function NotificationBell({ onOpenApproval }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const loadCount = () => api.get("/notifications/count").then((r) => setUnread(r.data.unread)).catch(() => {});
  const loadItems = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await loadItems();
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setUnread(0);
    setItems((its) => its.map((i) => ({ ...i, read: true })));
  };

  const openItem = async (n) => {
    if (!n.read) {
      api.post(`/notifications/${n.id}/read`).catch(() => {});
      setItems((its) => its.map((i) => (i.id === n.id ? { ...i, read: true } : i)));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    onOpenApproval?.(n.approval_id || null);
  };

  const timeAgo = (iso) => {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={toggle}
        className="relative grid place-items-center w-10 h-10 rounded-full bg-[#F1F5F0] hover:bg-[#62B848] hover:text-white transition-colors">
        <Bell size={17} />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1 -right-1 grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#62B848] text-white text-[10px] font-bold border-2 border-white">{unread}</span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel" className="absolute right-0 mt-2 w-[340px] max-h-[420px] overflow-hidden bg-white rounded-2xl border border-black/10 shadow-2xl z-50 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-black/5">
            <span className="font-display font-bold text-[#1A2421] text-sm">Notifications</span>
            <button data-testid="notif-mark-all" onClick={markAll} className="inline-flex items-center gap-1 text-xs font-bold text-[#224733] hover:text-[#62B848]"><CheckCheck size={14} /> Mark all read</button>
          </div>
          <div className="overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-[#4A5550]">You're all caught up.</div>
            ) : items.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-black/5 last:border-0 hover:bg-[#F1F5F0] transition-colors ${!n.read ? "bg-[#62B848]/[0.06]" : ""}`}>
                <div className="flex items-center gap-2">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[#62B848] shrink-0" />}
                  <span className="font-semibold text-sm text-[#1A2421]">{n.title}</span>
                </div>
                {n.body && <p className="text-xs text-[#4A5550] mt-0.5 ml-4">{n.body}</p>}
                <span className="text-[11px] text-[#9aa39e] ml-4 flex items-center gap-1">{timeAgo(n.created_at)} · <span className="text-[#224733] font-semibold">Open queue →</span></span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
